// components/DevolucionCompra/DevolucionCompraList.tsx
// Diseño corporativo estilo Notion/Linear
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { toast } from '../../utils/toast';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Box,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Stack,
  Tab,
  Tabs,
  alpha,
  Skeleton,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  AssignmentReturn as ReturnIcon,
  CheckCircleOutline as ProcessedIcon,
  Schedule as PendingIcon,
  PlayCircleOutline as ProcessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import devolucionCompraApi, { IDevolucionCompra } from '../../services/api/devolucionCompraApi';
import { DarkModeContext } from '../../context/darkModeContext';
import Swal from 'sweetalert2';
import { DetalleDevolucionModal } from '../../pages/devolucionesCompra/DetalleDevolucionModal';

// ── Paleta Notion/Linear ──
const P = {
  light: {
    bg: '#FFFFFF', bgS: '#F7F7F5', bgT: '#F1F1EF', text: '#37352F',
    textS: '#6B6B6B', textT: '#9B9A97', border: '#E9E9E7', borderH: '#D3D3D0',
    accent: '#2383E2', accentBg: '#E8F0FE', success: '#0F7B6C', successBg: '#DBEDDB',
    warning: '#D9730D', warningBg: '#FADEC9', error: '#E03E3E', errorBg: '#FBE4E4',
    shadow: '0 1px 3px rgba(0,0,0,0.04)', hover: 'rgba(55,53,47,0.04)',
  },
  dark: {
    bg: '#191919', bgS: '#202020', bgT: '#2B2B2B', text: '#E8E8E6',
    textS: '#9B9A97', textT: '#6B6B6B', border: '#333333', borderH: '#444444',
    accent: '#529CCA', accentBg: '#143A52', success: '#4DAB9A', successBg: '#1A2F2A',
    warning: '#C77D48', warningBg: '#2E2215', error: '#D44C47', errorBg: '#3D2020',
    shadow: '0 1px 3px rgba(0,0,0,0.2)', hover: 'rgba(255,255,255,0.04)',
  },
};

const getEstadoStyle = (estado: boolean, dark: boolean) => {
  const p = dark ? P.dark : P.light;
  if (estado === true) return { bg: p.successBg, color: p.success, dot: p.success, label: 'Procesada' };
  return { bg: p.warningBg, color: p.warning, dot: p.warning, label: 'Pendiente' };
};

// ── KPI Card ──
const KPICard: React.FC<{
  label: string; value: string | number; icon: React.ReactElement;
  accent?: string; dark: boolean;
}> = ({ label, value, icon, accent, dark }) => {
  const p = dark ? P.dark : P.light;
  return (
    <Box sx={{
      p: 2.5, borderRadius: '10px', border: `1px solid ${p.border}`, bgcolor: p.bg,
      boxShadow: p.shadow, flex: 1, minWidth: 160,
      transition: 'all 0.15s', '&:hover': { borderColor: p.borderH },
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '8px', bgcolor: accent || p.bgT,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: accent ? p.bg : p.textS, '& svg': { fontSize: 18 },
        }}>{icon}</Box>
        <Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: p.textT, letterSpacing: '0.02em' }}>{label}</Typography>
          <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: p.text, fontFeatureSettings: "'tnum'" }}>{value}</Typography>
        </Box>
      </Stack>
    </Box>
  );
};

// ── Action Button ──
const ActionBtn: React.FC<{
  tip: string; icon: React.ReactElement; onClick?: () => void;
  disabled?: boolean; dark: boolean; variant?: string;
}> = ({ tip, icon, onClick, disabled, dark, variant = 'default' }) => {
  const p = dark ? P.dark : P.light;
  const c: Record<string, any> = {
    default: { bg: p.bgT, hover: p.borderH, text: p.textS },
    accent: { bg: p.accentBg, hover: dark ? '#1C4466' : '#D4E4F7', text: p.accent },
    success: { bg: p.successBg, hover: dark ? '#1F3F38' : '#C4DDD7', text: p.success },
    warning: { bg: p.warningBg, hover: dark ? '#3A2A18' : '#F5D0A9', text: p.warning },
  };
  const col = c[variant] || c.default;
  return (
    <Tooltip title={tip} arrow><span>
      <IconButton onClick={onClick} disabled={disabled} size="small" sx={{
        width: 30, height: 30, borderRadius: '7px', color: disabled ? p.textT : col.text,
        bgcolor: disabled ? 'transparent' : col.bg, opacity: disabled ? 0.4 : 1,
        '& svg': { fontSize: 16 }, '&:hover': { bgcolor: disabled ? 'transparent' : col.hover },
      }}>{icon}</IconButton>
    </span></Tooltip>
  );
};

// ── MAIN COMPONENT ──
export const DevolucionCompraList: React.FC = () => {
  const navigate = useNavigate();
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const [devoluciones, setDevoluciones] = useState<IDevolucionCompra[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [tabFilter, setTabFilter] = useState('todas');
  const [selectedDevolucion, setSelectedDevolucion] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [procesando, setProcesando] = useState<Set<number>>(new Set());

  useEffect(() => { loadDevoluciones(); }, []);

  const loadDevoluciones = async () => {
    try {
      setLoading(true);
      const data = await devolucionCompraApi.findAll();
      setDevoluciones(data || []);
    } catch {
      toast.error('No se pudieron cargar las devoluciones');
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalle = async (id: number) => {
    try {
      const detalle = await devolucionCompraApi.findOne(id);
      setSelectedDevolucion(detalle);
      setModalOpen(true);
    } catch {
      toast.error('No se pudo cargar el detalle');
    }
  };

  const handleProcesar = async (devolucion: IDevolucionCompra) => {
    const confirm = await Swal.fire({
      title: '¿Procesar devolución?',
      html: `<span>OC #<b>${devolucion.OrdenCompra}</b> — esta acción no se puede deshacer.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, procesar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0F7B6C',
    });
    if (!confirm.isConfirmed) return;
    setProcesando(prev => new Set(prev).add(devolucion.Id));
    try {
      await devolucionCompraApi.procesarDevolucion(devolucion.Id);
      await loadDevoluciones();
      toast.success('La devolución fue procesada exitosamente');
    } catch (e: any) {
      toast.error('Error');
    } finally {
      setProcesando(prev => { const s = new Set(prev); s.delete(devolucion.Id); return s; });
    }
  };

  const handleModalProcesar = async (id: number) => {
    setModalOpen(false);
    const dev = devoluciones.find(d => d.Id === id);
    if (dev) await handleProcesar(dev);
    else await loadDevoluciones();
  };

  // ── KPIs ──
  const kpis = useMemo(() => ({
    total: devoluciones.length,
    pendientes: devoluciones.filter(d => d.Estado === false).length,
    procesadas: devoluciones.filter(d => d.Estado === true).length,
  }), [devoluciones]);

  // ── Filtered list ──
  const filtered = useMemo(() => {
    let list = devoluciones;
    if (tabFilter === 'pendientes') list = list.filter(d => d.Estado === false);
    else if (tabFilter === 'procesadas') list = list.filter(d => d.Estado === true);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(d =>
        d.OrdenCompra.toLowerCase().includes(q) ||
        d.Id.toString().includes(q)
      );
    }
    return list;
  }, [devoluciones, tabFilter, searchTerm]);

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const cellSx = {
    borderBottom: `1px solid ${p.border}`,
    color: p.text,
    fontSize: '0.875rem',
    py: 1.5,
  };
  const headCellSx = {
    ...cellSx,
    color: p.textT,
    fontWeight: 600,
    fontSize: '0.72rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    bgcolor: p.bgS,
    borderTop: `1px solid ${p.border}`,
  };

  return (
    <>
    <Box sx={{ bgcolor: p.bgS, minHeight: '100vh', px: { xs: 2, md: 3 }, py: 3 }}>

        {/* ── KPIs ── */}
        <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <KPICard label="TOTAL DEVOLUCIONES" value={loading ? '—' : kpis.total} icon={<ReturnIcon />} dark={darkMode} />
          <KPICard label="PENDIENTES" value={loading ? '—' : kpis.pendientes} icon={<PendingIcon />} accent={p.warning} dark={darkMode} />
          <KPICard label="PROCESADAS" value={loading ? '—' : kpis.procesadas} icon={<ProcessedIcon />} accent={p.success} dark={darkMode} />
        </Stack>

        {/* ── Header ── */}
        <Box sx={{
          bgcolor: p.bg, border: `1px solid ${p.border}`, borderRadius: '10px 10px 0 0',
          px: 2.5, pt: 2, pb: 0, boxShadow: p.shadow,
        }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: p.text }}>
                Devoluciones de Compra
              </Typography>
              {!loading && (
                <Box sx={{ px: 1, py: 0.25, borderRadius: '5px', bgcolor: p.bgT, color: p.textS, fontSize: '0.78rem', fontWeight: 600 }}>
                  {filtered.length}
                </Box>
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Actualizar" arrow>
                <IconButton onClick={loadDevoluciones} size="small" sx={{ color: p.textS, bgcolor: p.bgT, borderRadius: '7px', width: 32, height: 32, '& svg': { fontSize: 17 } }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/devoluciones-compra/new')}
                size="small"
                sx={{
                  bgcolor: p.accent, color: '#fff', borderRadius: '7px', textTransform: 'none',
                  fontWeight: 600, fontSize: '0.82rem', boxShadow: 'none',
                  '&:hover': { bgcolor: darkMode ? '#3A7BA8' : '#1A6DC0', boxShadow: 'none' },
                }}
              >
                Nueva Devolución
              </Button>
            </Stack>
          </Stack>

          {/* ── Tabs ── */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Tabs
              value={tabFilter}
              onChange={(_, v) => { setTabFilter(v); setPage(0); }}
              sx={{
                minHeight: 0,
                '& .MuiTab-root': { minHeight: 36, py: 0.5, px: 1.5, textTransform: 'none', fontWeight: 500, fontSize: '0.85rem', color: p.textS, minWidth: 0 },
                '& .Mui-selected': { color: p.text, fontWeight: 600 },
                '& .MuiTabs-indicator': { bgcolor: p.text, height: 2 },
              }}
            >
              <Tab label="Todas" value="todas" />
              <Tab label={`Pendientes${kpis.pendientes > 0 ? ` (${kpis.pendientes})` : ''}`} value="pendientes" />
              <Tab label="Procesadas" value="procesadas" />
            </Tabs>
            <TextField
              size="small"
              placeholder="Buscar OC o ID..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: p.textT }} /></InputAdornment>,
                sx: { bgcolor: p.bgS, border: `1px solid ${p.border}`, borderRadius: '7px', fontSize: '0.85rem', color: p.text, '& fieldset': { border: 'none' } },
              }}
              sx={{ '& .MuiInputBase-input': { py: 0.75 }, width: 220 }}
            />
          </Stack>
        </Box>

        {/* ── Table ── */}
        <Paper elevation={0} sx={{ bgcolor: p.bg, border: `1px solid ${p.border}`, borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>ID</TableCell>
                  <TableCell sx={headCellSx}>Orden de Compra</TableCell>
                  <TableCell sx={headCellSx} align="center">Artículos</TableCell>
                  <TableCell sx={headCellSx}>Estado</TableCell>
                  <TableCell sx={headCellSx} align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {[1, 2, 3, 4, 5].map(j => (
                        <TableCell key={j} sx={cellSx}>
                          <Skeleton variant="text" width={j === 5 ? 60 : '80%'} height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ ...cellSx, py: 6 }}>
                      <Stack alignItems="center" spacing={1}>
                        <ReturnIcon sx={{ fontSize: 36, color: p.textT }} />
                        <Typography sx={{ color: p.textT, fontSize: '0.9rem' }}>
                          {searchTerm ? 'Sin resultados para la búsqueda' : 'No hay devoluciones registradas'}
                        </Typography>
                        {!searchTerm && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/devoluciones-compra/new')}
                            sx={{ mt: 1, borderRadius: '7px', textTransform: 'none', borderColor: p.border, color: p.textS }}
                          >
                            Nueva Devolución
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map(dev => {
                    const est = getEstadoStyle(dev.Estado, darkMode);
                    const isProcesando = procesando.has(dev.Id);
                    return (
                      <TableRow
                        key={dev.Id}
                        sx={{ '&:hover': { bgcolor: p.hover }, transition: 'background 0.1s' }}
                      >
                        <TableCell sx={cellSx}>
                          <Typography sx={{ fontSize: '0.8rem', color: p.textT, fontWeight: 500 }}>
                            #{dev.Id}
                          </Typography>
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <Typography sx={{ fontWeight: 600, color: p.text }}>
                            OC-{dev.OrdenCompra}
                          </Typography>
                        </TableCell>
                        <TableCell sx={cellSx} align="center">
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 28, height: 28, borderRadius: '50%', bgcolor: p.bgT,
                            color: p.textS, fontSize: '0.82rem', fontWeight: 600,
                          }}>
                            {dev.detalles?.length ?? 0}
                          </Box>
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.75,
                            px: 1.25, py: 0.4, borderRadius: '5px',
                            bgcolor: est.bg, color: est.color, fontSize: '0.78rem', fontWeight: 600,
                          }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: est.dot, flexShrink: 0 }} />
                            {est.label}
                          </Box>
                        </TableCell>
                        <TableCell sx={cellSx} align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <ActionBtn
                              tip="Ver detalle"
                              icon={<VisibilityIcon />}
                              onClick={() => handleVerDetalle(dev.Id)}
                              dark={darkMode}
                              variant="accent"
                            />
                            {dev.Estado === false && (
                              <ActionBtn
                                tip="Procesar devolución"
                                icon={isProcesando ? <CircularProgress size={14} /> : <ProcessIcon />}
                                onClick={() => !isProcesando && handleProcesar(dev)}
                                disabled={isProcesando}
                                dark={darkMode}
                                variant="success"
                              />
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && filtered.length > 0 && (
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Filas:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{ borderTop: `1px solid ${p.border}`, color: p.textS, '& .MuiTablePagination-select': { color: p.text } }}
            />
          )}
        </Paper>
      </Box>

      <DetalleDevolucionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        devolucion={selectedDevolucion}
        dark={darkMode}
        onProcesar={handleModalProcesar}
        onRefresh={loadDevoluciones}
      />
    </>
  );
};

export default DevolucionCompraList;
